import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';

import * as Styled from './confirm-dialog.style';

export type ConfirmDialogProps = {
  open: boolean;
  // opt-in for the caller that raises this from inside a PopupMenu
  abovePopups?: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: Colors;
  // for a message whose action is unavailable — the cancel button is then the only way out
  hideConfirm?: boolean;
};

// Only for synchronous yes/no confirmations — dialogs with pending state,
// extra inputs, or a third option stay bespoke.
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  abovePopups,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  confirmColor = Colors.Error,
  hideConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.Dialog
      $abovePopups={abovePopups}
      open={open}
      onClose={onCancel}
      variant={DialogVariantOptions.ExtraSmall}
    >
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
        <Typography variant="body2">{message}</Typography>
      </Styled.Header>

      <Styled.Interactions>
        <Button
          variant={Variants.Contained}
          size={Sizes.Medium}
          color={Colors.TextPrimary}
          onClick={onCancel}
        >
          {cancelLabel ?? t('general.cancel')}
        </Button>
        {!hideConfirm && (
          <Button size={Sizes.Medium} color={confirmColor} onClick={onConfirm}>
            {confirmLabel ?? t('general.delete')}
          </Button>
        )}
      </Styled.Interactions>
    </Styled.Dialog>
  );
};
